import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Bot, Plus, Play, Pause, Copy, Trash2, MoreVertical, Edit3, Search, BarChart3, ChevronRight, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

const TRIGGER_TYPES = [
  { value: "any_message", label: "Any incoming message" },
  { value: "first_message", label: "First incoming message" },
  { value: "exact_keyword", label: "Exact keyword" },
  { value: "contains_keyword", label: "Contains keyword" },
  { value: "starts_with", label: "Starts with keyword" },
  { value: "button_reply", label: "Button reply" },
  { value: "list_reply", label: "List reply" },
];

const STATUS_META = {
  draft:     { label: "Draft",     className: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  published: { label: "Published", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" },
  paused:    { label: "Paused",    className: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" },
  archived:  { label: "Archived",  className: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold ${m.className}`} data-testid={`bot-status-${status}`}>
      {m.label}
    </span>
  );
}

export default function Chatbots() {
  const nav = useNavigate();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chatbots");
      setBots(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load chatbots");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/whatsapp/phone-numbers")
      .then(r => setPhoneNumbers((r.data || []).filter(n => n.is_active !== false)))
      .catch(() => setPhoneNumbers([]));
  }, []);

  const filtered = bots.filter(b => {
    const matchesQ = !q || (b.name?.toLowerCase().includes(q.toLowerCase()) || b.description?.toLowerCase().includes(q.toLowerCase()));
    const matchesS = statusFilter === "all" || b.status === statusFilter;
    return matchesQ && matchesS;
  });

  const doAction = async (id, action, label) => {
    try {
      await api.post(`/chatbots/${id}/${action}`);
      toast.success(label);
      load();
    } catch (e) {
      const err = e.response?.data?.detail;
      if (Array.isArray(err?.errors)) {
        toast.error(`Validation failed: ${err.errors[0]}`);
      } else {
        toast.error(typeof err === "string" ? err : `${label} failed`);
      }
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this chatbot and all its versions? This cannot be undone.")) return;
    try {
      await api.delete(`/chatbots/${id}`);
      toast.success("Chatbot deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="chatbots-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Chatbot</div>
          <h1 className="text-2xl font-black tracking-tighter flex items-center gap-2">
            <Bot className="h-6 w-6 text-orange-500" /> My Chatbots
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Configurable WhatsApp auto-reply flows. Design once, publish, and let the bot answer 24×7.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm gap-2" data-testid="create-chatbot-btn">
              <Plus className="h-4 w-4" /> Create Chatbot
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-sm">
            <CreateForm phoneNumbers={phoneNumbers} onDone={(bot) => {
              setCreateOpen(false);
              load();
              if (bot?.id) nav(`/chatbots/${bot.id}/builder`);
            }} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="rounded-sm shadow-none">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search chatbots…"
              className="h-8 pl-7 rounded-sm text-xs" data-testid="chatbot-search" />
          </div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider" data-testid="chatbot-status-filter">
            {["all", "draft", "published", "paused"].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-2 py-1 rounded-sm border transition ${statusFilter === s ? "bg-orange-500 border-orange-500 text-white" : "border-muted-foreground/20 text-muted-foreground hover:bg-muted"}`}
                data-testid={`filter-${s}`}>
                {s === "all" ? "All" : STATUS_META[s]?.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="text-center text-xs text-muted-foreground p-8">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-sm shadow-none border-dashed">
          <CardContent className="p-10 text-center space-y-2" data-testid="chatbot-empty-state">
            <Bot className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <div className="text-sm font-semibold">No chatbots yet</div>
            <div className="text-xs text-muted-foreground max-w-md mx-auto">
              Create your first chatbot to automatically respond to incoming WhatsApp messages 24×7.
            </div>
            <Button className="rounded-sm mt-2" onClick={() => setCreateOpen(true)} data-testid="empty-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Create your first chatbot
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(b => (
            <Card key={b.id} className="rounded-sm shadow-none hover:border-orange-300 transition group" data-testid={`bot-card-${b.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className="h-9 w-9 rounded-sm bg-gradient-to-br from-orange-400 to-amber-600 text-white flex items-center justify-center shrink-0">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate" data-testid={`bot-name-${b.id}`}>{b.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{b.description || "No description"}</div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`bot-menu-${b.id}`}>
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="text-xs">
                      <DropdownMenuItem onClick={() => nav(`/chatbots/${b.id}/builder`)} data-testid={`menu-open-builder-${b.id}`}>
                        <Edit3 className="h-3.5 w-3.5 mr-2" /> Open Builder
                      </DropdownMenuItem>
                      {b.status !== "published" && (
                        <DropdownMenuItem onClick={() => doAction(b.id, "publish", "Published")}>
                          <Play className="h-3.5 w-3.5 mr-2" /> Publish
                        </DropdownMenuItem>
                      )}
                      {b.status === "published" && (
                        <DropdownMenuItem onClick={() => doAction(b.id, "pause", "Paused")}>
                          <Pause className="h-3.5 w-3.5 mr-2" /> Pause
                        </DropdownMenuItem>
                      )}
                      {b.status === "paused" && (
                        <DropdownMenuItem onClick={() => doAction(b.id, "resume", "Resumed")}>
                          <Play className="h-3.5 w-3.5 mr-2" /> Resume
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => doAction(b.id, "clone", "Duplicated")}>
                        <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => doDelete(b.id)} className="text-red-600" data-testid={`bot-delete-${b.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <StatusPill status={b.status} />
                  <Badge variant="outline" className="text-[10px] rounded-sm h-4 px-1">
                    Trigger: {b.trigger_type?.replace(/_/g," ")}
                  </Badge>
                  {b.phone_number_id ? (
                    <Badge variant="outline" className="text-[10px] rounded-sm h-4 px-1 font-mono">
                      #{String(b.phone_number_id).slice(-4)}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] rounded-sm h-4 px-1 text-muted-foreground">
                      Any number
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 text-center pt-1 border-t">
                  <div>
                    <div className="text-lg font-bold leading-none">{b.sessions_total || 0}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Sessions</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold leading-none text-emerald-600">{b.sessions_completed || 0}</div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Completed</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold leading-none text-muted-foreground">
                      {(b.trigger_keywords || []).length}
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Keywords</div>
                  </div>
                </div>

                <div className="pt-2 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 rounded-sm h-8 text-xs"
                    onClick={() => nav(`/chatbots/${b.id}/builder`)} data-testid={`open-builder-${b.id}`}>
                    <Edit3 className="h-3 w-3 mr-1" /> Open Builder
                    <ChevronRight className="h-3 w-3 ml-auto" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateForm({ phoneNumbers, onDone }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    phone_number_id: "",
    trigger_type: "exact_keyword",
    trigger_keywords: "Hi, Hello, Hey",
    welcome_message: "Hi {{contact.name}}, welcome to {{tenant.name}}. How may I help you?",
    fallback_message: "Sorry, I did not understand. Type 'menu' anytime.",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        phone_number_id: form.phone_number_id || null,
        trigger_keywords: form.trigger_keywords.split(",").map(s=>s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/chatbots", payload);
      toast.success(`Chatbot "${data.name}" created — opening builder`);
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg font-black tracking-tight">Create Chatbot</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Chatbot Name</Label>
          <Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})}
            placeholder="e.g. V-Mart Welcome Bot" className="rounded-sm h-8 text-xs mt-1"
            data-testid="new-bot-name" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Description</Label>
          <Input value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
            className="rounded-sm h-8 text-xs mt-1" data-testid="new-bot-desc" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Connected WhatsApp Number</Label>
          <select value={form.phone_number_id} onChange={e=>setForm({...form, phone_number_id: e.target.value})}
            className="w-full h-8 rounded-sm border bg-background text-xs px-2 mt-1"
            data-testid="new-bot-phone-select">
            <option value="">Any number (all inbound)</option>
            {phoneNumbers.map(p => (
              <option key={p.phone_number_id} value={p.phone_number_id}>
                {p.is_primary ? "★ " : ""}{p.display_phone_number || p.phone_number_id.slice(-6)}
              </option>
            ))}
          </select>
          {phoneNumbers.length === 0 && (
            <div className="text-[10px] text-muted-foreground mt-1">
              No WhatsApp numbers connected yet — the bot will run on any inbound WhatsApp message.
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Trigger Type</Label>
            <select value={form.trigger_type} onChange={e=>setForm({...form, trigger_type: e.target.value})}
              className="w-full h-8 rounded-sm border bg-background text-xs px-2 mt-1"
              data-testid="new-bot-trigger-type">
              {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Keywords (comma-separated)</Label>
            <Input value={form.trigger_keywords} onChange={e=>setForm({...form, trigger_keywords: e.target.value})}
              placeholder="Hi, Hello, Hey" className="rounded-sm h-8 text-xs mt-1"
              data-testid="new-bot-keywords" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Welcome Message</Label>
          <Textarea value={form.welcome_message} onChange={e=>setForm({...form, welcome_message: e.target.value})}
            rows={2} className="rounded-sm text-xs mt-1" data-testid="new-bot-welcome" />
          <div className="text-[10px] text-muted-foreground mt-1">
            Variables: <code>{"{{contact.name}}"}</code>, <code>{"{{tenant.name}}"}</code>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Fallback Message</Label>
          <Textarea value={form.fallback_message} onChange={e=>setForm({...form, fallback_message: e.target.value})}
            rows={2} className="rounded-sm text-xs mt-1" data-testid="new-bot-fallback" />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} className="rounded-sm gap-1" data-testid="new-bot-submit">
          <MessageSquareText className="h-3.5 w-3.5" />
          {saving ? "Creating…" : "Create & Open Builder"}
        </Button>
      </DialogFooter>
    </>
  );
}
